/**
 * @fileoverview Filter Presets Component
 * 
 * This component manages filter presets including applying, saving, and deleting presets,
 * as well as import/export functionality.
 * 
 * Requirements: 3.5, 3.6, 10.3
 */

import React, { useState, useCallback } from 'react';

/**
 * Filter Presets Component
 * 
 * @param {Object} props - Component props
 * @param {FilterPreset[]} props.presets - Available filter presets
 * @param {string} props.activePreset - Currently active preset ID
 * @param {function(FilterPreset): void} props.onPresetApply - Handler for applying presets
 * @param {function(string, string): void} props.onPresetSave - Handler for saving presets
 * @param {function(string): void} props.onPresetDelete - Handler for deleting presets
 * @param {function(): void} props.onExport - Handler for exporting filters
 * @param {function(File): void} props.onImport - Handler for importing filters
 * @param {string} props.testId - Test ID
 */
export const FilterPresets = ({
  presets,
  activePreset,
  onPresetApply,
  onPresetSave,
  onPresetDelete,
  onExport,
  onImport,
  testId = 'filter-presets'
}) => {
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [savePresetName, setSavePresetName] = useState('');
  const [savePresetDescription, setSavePresetDescription] = useState('');
  const [showImportDialog, setShowImportDialog] = useState(false);

  /**
   * Handle preset save
   */
  const handlePresetSave = useCallback(() => {
    if (!savePresetName.trim()) {
      return;
    }

    onPresetSave(savePresetName.trim(), savePresetDescription.trim());
    
    // Reset form
    setSavePresetName('');
    setSavePresetDescription('');
    setShowSaveDialog(false);
  }, [savePresetName, savePresetDescription, onPresetSave]);

  /**
   * Handle file import
   */
  const handleFileImport = useCallback((event) => {
    const file = event.target.files?.[0];
    if (file && onImport) {
      onImport(file);
      setShowImportDialog(false);
    }
    // Reset input
    event.target.value = '';
  }, [onImport]);

  /**
   * Handle preset deletion with confirmation
   */
  const handlePresetDelete = useCallback((preset) => {
    if (window.confirm(`Are you sure you want to delete the preset "${preset.name}"?`)) {
      onPresetDelete(preset.id);
    }
  }, [onPresetDelete]);

  if (!presets || presets.length === 0) {
    return (
      <div className="filter-presets empty" data-testid={testId}>
        <div className="presets-header">
          <h3>Filter Presets</h3>
          <div className="presets-actions">
            {onPresetSave && (
              <button
                className="preset-save-button"
                onClick={() => setShowSaveDialog(true)}
                data-testid={`${testId}-save-button`}
              >
                Save Current Filters
              </button>
            )}
            
            {onImport && (
              <button
                className="preset-import-button"
                onClick={() => setShowImportDialog(true)}
                data-testid={`${testId}-import-button`}
              >
                Import
              </button>
            )}
          </div>
        </div>
        
        <p className="no-presets-message">
          No filter presets available. Save your current filters to create a preset.
        </p>

        {/* Save Dialog */}
        {showSaveDialog && (
          <div className="preset-save-dialog" data-testid={`${testId}-save-dialog`}>
            <div className="dialog-content">
              <h4>Save Filter Preset</h4>
              
              <div className="form-group">
                <label htmlFor="preset-name">Preset Name *</label>
                <input
                  id="preset-name"
                  type="text"
                  value={savePresetName}
                  onChange={(e) => setSavePresetName(e.target.value)}
                  placeholder="Enter preset name..."
                  data-testid={`${testId}-save-name`}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="preset-description">Description</label>
                <textarea
                  id="preset-description"
                  value={savePresetDescription}
                  onChange={(e) => setSavePresetDescription(e.target.value)}
                  placeholder="Optional description..."
                  rows={3}
                  data-testid={`${testId}-save-description`}
                />
              </div>
              
              <div className="dialog-actions">
                <button
                  className="cancel-button"
                  onClick={() => setShowSaveDialog(false)}
                  data-testid={`${testId}-save-cancel`}
                >
                  Cancel
                </button>
                <button
                  className="save-button"
                  onClick={handlePresetSave}
                  disabled={!savePresetName.trim()}
                  data-testid={`${testId}-save-confirm`}
                >
                  Save Preset
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Import Dialog */}
        {showImportDialog && (
          <div className="preset-import-dialog" data-testid={`${testId}-import-dialog`}>
            <div className="dialog-content">
              <h4>Import Filter Configuration</h4>
              
              <div className="form-group">
                <label htmlFor="import-file">Select JSON file to import</label>
                <input
                  id="import-file"
                  type="file"
                  accept=".json"
                  onChange={handleFileImport}
                  data-testid={`${testId}-import-file`}
                />
              </div>
              
              <div className="dialog-actions">
                <button
                  className="cancel-button"
                  onClick={() => setShowImportDialog(false)}
                  data-testid={`${testId}-import-cancel`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="filter-presets" data-testid={testId}>
      <div className="presets-header">
        <h3>Filter Presets</h3>
        <div className="presets-actions">
          {onPresetSave && (
            <button
              className="preset-save-button"
              onClick={() => setShowSaveDialog(true)}
              data-testid={`${testId}-save-button`}
            >
              Save Current
            </button>
          )}
          
          {onExport && (
            <button
              className="preset-export-button"
              onClick={onExport}
              data-testid={`${testId}-export-button`}
            >
              Export
            </button>
          )}
          
          {onImport && (
            <button
              className="preset-import-button"
              onClick={() => setShowImportDialog(true)}
              data-testid={`${testId}-import-button`}
            >
              Import
            </button>
          )}
        </div>
      </div>

      <div className="presets-list">
        {presets.map((preset) => (
          <div
            key={preset.id}
            className={`preset-item ${activePreset === preset.id ? 'active' : ''}`}
            data-testid={`${testId}-item-${preset.id}`}
          >
            <div className="preset-info">
              <div className="preset-name">{preset.name}</div>
              {preset.description && (
                <div className="preset-description">{preset.description}</div>
              )}
              <div className="preset-meta">
                {preset.filters.length} filter{preset.filters.length !== 1 ? 's' : ''}
                {preset.createdAt && (
                  <span className="preset-date">
                    • Created {new Date(preset.createdAt).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>

            <div className="preset-actions">
              <button
                className="preset-apply-button"
                onClick={() => onPresetApply(preset)}
                disabled={activePreset === preset.id}
                data-testid={`${testId}-apply-${preset.id}`}
              >
                {activePreset === preset.id ? 'Active' : 'Apply'}
              </button>
              
              {onPresetDelete && (
                <button
                  className="preset-delete-button"
                  onClick={() => handlePresetDelete(preset)}
                  title={`Delete preset: ${preset.name}`}
                  data-testid={`${testId}-delete-${preset.id}`}
                >
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Save Dialog */}
      {showSaveDialog && (
        <div className="preset-save-dialog" data-testid={`${testId}-save-dialog`}>
          <div className="dialog-overlay" onClick={() => setShowSaveDialog(false)} />
          <div className="dialog-content">
            <h4>Save Filter Preset</h4>
            
            <div className="form-group">
              <label htmlFor="preset-name">Preset Name *</label>
              <input
                id="preset-name"
                type="text"
                value={savePresetName}
                onChange={(e) => setSavePresetName(e.target.value)}
                placeholder="Enter preset name..."
                data-testid={`${testId}-save-name`}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="preset-description">Description</label>
              <textarea
                id="preset-description"
                value={savePresetDescription}
                onChange={(e) => setSavePresetDescription(e.target.value)}
                placeholder="Optional description..."
                rows={3}
                data-testid={`${testId}-save-description`}
              />
            </div>
            
            <div className="dialog-actions">
              <button
                className="cancel-button"
                onClick={() => setShowSaveDialog(false)}
                data-testid={`${testId}-save-cancel`}
              >
                Cancel
              </button>
              <button
                className="save-button"
                onClick={handlePresetSave}
                disabled={!savePresetName.trim()}
                data-testid={`${testId}-save-confirm`}
              >
                Save Preset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Dialog */}
      {showImportDialog && (
        <div className="preset-import-dialog" data-testid={`${testId}-import-dialog`}>
          <div className="dialog-overlay" onClick={() => setShowImportDialog(false)} />
          <div className="dialog-content">
            <h4>Import Filter Configuration</h4>
            
            <div className="form-group">
              <label htmlFor="import-file">Select JSON file to import</label>
              <input
                id="import-file"
                type="file"
                accept=".json"
                onChange={handleFileImport}
                data-testid={`${testId}-import-file`}
              />
            </div>
            
            <p className="import-note">
              Import will add new presets and filters to your current configuration.
              Existing presets will not be overwritten.
            </p>
            
            <div className="dialog-actions">
              <button
                className="cancel-button"
                onClick={() => setShowImportDialog(false)}
                data-testid={`${testId}-import-cancel`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
